// Basket operation utilities

import type { Basket, Food } from '../types.js'

/**
 * Add grams to the basket
 */
export function addGrams(basket: Basket, foodId: string, grams: number): Basket {
  return {
    ...basket,
    [foodId]: (basket[foodId] || 0) + grams
  }
}

/**
 * Remove grams from the basket
 */
export function removeGrams(basket: Basket, foodId: string, grams: number): Basket {
  const currentGrams = basket[foodId] || 0
  const newGrams = Math.max(0, currentGrams - grams)

  return {
    ...basket,
    [foodId]: newGrams
  }
}

/**
 * Remove all grams of a food from the basket
 */
export function removeFood(basket: Basket, foodId: string): Basket {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [foodId]: _, ...rest } = basket
  return rest
}

/**
 * Clear the entire basket
 */
export function clearBasket(): Basket {
  return {}
}

/**
 * Check if basket is empty
 */
export function isBasketEmpty(basket: Basket): boolean {
  return Object.keys(basket).length === 0
}

/**
 * Get total weight of items in basket
 */
export function getBasketWeight(basket: Basket): number {
  return Object.values(basket).reduce((sum, grams) => sum + grams, 0)
}

/**
 * Validate basket (ensure no negative grams)
 */
export function validateBasket(basket: Basket): Basket {
  const validated: Basket = {}

  for (const [foodId, grams] of Object.entries(basket)) {
    if (grams >= 0) {
      validated[foodId] = Math.max(0, grams)
    }
  }

  return validated
}

/**
 * Serialize basket for localStorage
 */
export function serializeBasket(basket: Basket): string {
  return JSON.stringify(basket)
}

/**
 * Deserialize basket from localStorage
 */
export function deserializeBasket(serialized: string): Basket {
  try {
    const basket = JSON.parse(serialized)
    return validateBasket(basket)
  } catch (error) {
    console.warn('Failed to deserialize basket:', error)
    return {}
  }
}

/**
 * Set exact grams for a food in the basket
 */
export function setFoodGrams(basket: Basket, foodId: string, grams: number): Basket {
  return {
    ...basket,
    [foodId]: Math.max(0, grams)
  }
}

/**
 * Add portion-sized grams to the basket (convenience function)
 */
export function addPortion(basket: Basket, foodId: string, food: Food): Basket {
  return addGrams(basket, foodId, food.portionGrams)
}

/**
 * Remove portion-sized grams from the basket (convenience function)
 */
export function removePortion(basket: Basket, foodId: string, food: Food): Basket {
  return removeGrams(basket, foodId, food.portionGrams)
}